import BenchmarkBar from "../BenchmarkBar"
import { cn } from "~/ui-rtk/utils/tailwind-utils"

export interface BenchmarkStatCardProps {
  /** Label for the conversion step (e.g., "Collection → Cart") */
  label: string
  /** Current conversion rate value (percentage) */
  currentValue: number
  /** Abandonment rate (percentage) */
  abandonRate: number
  /** Benchmark data for the bar visualization */
  benchmark?: {
    worst: number
    avg: number
    best: number
  } | null
  /** Toggle benchmark bar visibility */
  showBenchmark?: boolean
  /** Color for the main value based on performance */
  valueColor?: "green" | "red" | "white"
}

/**
 * BenchmarkStatCard Component
 *
 * A conversion metric card that displays:
 * - Left side: Metric label, current value, abandon rate
 * - Right side: Benchmark bar visualization (when enabled)
 *
 * Layout adapts based on whether benchmarks are shown:
 * - Benchmarks OFF: Simple single-column layout
 * - Benchmarks ON: Two-column layout with bar on right
 */
/**
 * Validates benchmark data to ensure it's renderable.
 * Returns false if data is missing or would cause rendering issues.
 */
const isValidBenchmark = (
  benchmark: { worst: number; avg: number; best: number } | null | undefined,
): benchmark is { worst: number; avg: number; best: number } => {
  if (!benchmark) return false

  // Check for NaN or undefined values
  if (
    typeof benchmark.worst !== "number" ||
    typeof benchmark.avg !== "number" ||
    typeof benchmark.best !== "number" ||
    isNaN(benchmark.worst) ||
    isNaN(benchmark.avg) ||
    isNaN(benchmark.best)
  ) {
    return false
  }

  // Check for valid range (best should be >= worst for meaningful visualization)
  // Allow equal values (will show equal segments)
  if (benchmark.best < benchmark.worst) {
    return false
  }

  return true
}

export default function BenchmarkStatCard({
  label,
  currentValue,
  abandonRate,
  benchmark,
  showBenchmark = false,
  valueColor = "green",
}: BenchmarkStatCardProps) {
  const valueColorClass = {
    green: "text-green-400",
    red: "text-red-400",
    white: "text-white",
  }[valueColor]

  // Only show benchmark if enabled AND data is valid
  const hasBenchmarkData = showBenchmark && isValidBenchmark(benchmark)

  return (
    <div
      className={cn(
        "bg-system-blue-900/50 rounded-lg p-4",
        // When benchmarks are shown, use flex layout and add min-width
        hasBenchmarkData && "flex items-start gap-6 min-w-[350px]",
      )}
    >
      {/* Metrics section - IDENTICAL structure regardless of benchmark state */}
      <div>
        <div className="text-sm text-system-gray-400 mb-1">{label}</div>
        <div className={cn("text-2xl font-bold", valueColorClass)}>
          {(currentValue ?? 0).toFixed(1)}%
        </div>
        <div className="text-xs text-red-400 mt-1">
          {(abandonRate ?? 0).toFixed(1)}% abandon
        </div>
      </div>

      {/* Benchmark bar column - only rendered when enabled */}
      {hasBenchmarkData && (
        <div className="flex-1 min-w-[180px]">
          <BenchmarkBar
            currentValue={currentValue}
            worstValue={benchmark.worst}
            avgValue={benchmark.avg}
            bestValue={benchmark.best}
          />
        </div>
      )}
    </div>
  )
}
